// @ts-nocheck
// ============================================================
// VISION DASHBOARD — Fase 9.3 / 9.15
// /vision/cameras — NOC industrial enterprise
// ============================================================
import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Camera, Eye, Shield, Activity, Cpu,
  AlertTriangle, CheckCircle2, Zap, Plus, Settings,
} from 'lucide-react'
import { useVisionStore, selectActiveEvents, selectPendingViolations } from '@/store/visionStore'
import { startVisionEngine } from '@/core/vision/visionEngine'
import { useAuth } from '@/contexts/AuthContext'
import CameraWall from '@/components/vision/CameraWall'
import VisionTimeline from '@/components/vision/VisionTimeline'
import { DetectionBadge, PPEViolationCard, StreamStatus } from '@/design-system/vision'
import { evaluateVisionRisk } from '@/core/vision-ai/riskVisionEngine'
import { SURFACE, TEXT } from '@/design-system/tokens'
import { FF } from '@/core/featureFlags'
import type { CameraDevice } from '@/types/vision'

function VisionKPIBar() {
  const { cameras, events, violations } = useVisionStore()
  const activeEvents  = selectActiveEvents({ events } as any)
  const pendingPPE    = selectPendingViolations({ violations } as any)
  const onlineCount   = cameras.filter(c => c.status === 'online').length

  return (
    <div className="grid grid-cols-5 gap-3">
      {[
        { label: 'Câmeras Online',  value: onlineCount,            total: cameras.length,   icon: <Camera size={13} />,    color: onlineCount === cameras.length ? '#34d399' : '#fbbf24' },
        { label: 'Eventos Ativos',  value: activeEvents.length,    total: undefined,         icon: <Activity size={13} />,  color: activeEvents.length > 0 ? '#f87171' : '#34d399' },
        { label: 'Violações EPI',   value: pendingPPE.length,      total: undefined,         icon: <Shield size={13} />,    color: pendingPPE.length > 0 ? '#ef4444' : '#34d399' },
        { label: 'Críticos',        value: activeEvents.filter(e => e.severity === 'critical' || e.severity === 'emergency').length, total: undefined, icon: <AlertTriangle size={13} />, color: '#f87171' },
        { label: 'Cobertura',       value: cameras.length > 0 ? Math.round(onlineCount / cameras.length * 100) : 100, total: undefined, icon: <Eye size={13} />, color: '#60a5fa', suffix: '%' },
      ].map((kpi, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
          className="rounded-xl px-3 py-2.5"
          style={{ background: SURFACE.card, border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <span style={{ color: kpi.color }}>{kpi.icon}</span>
            <span className="text-[9px]" style={{ color: TEXT.faint }}>{kpi.label}</span>
          </div>
          <div className="text-xl font-black" style={{ color: kpi.color }}>
            {kpi.value}{kpi.suffix ?? ''}
            {kpi.total !== undefined && (
              <span className="text-sm font-normal ml-1" style={{ color: TEXT.faint }}>/{kpi.total}</span>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  )
}

function VisionRiskPanel() {
  const { cameras, events, violations } = useVisionStore()

  const offlineCameras = cameras.filter(c => c.status === 'offline').length
  const riskScore = evaluateVisionRisk({
    obraId:         '',
    cameras,
    recentEvents:   events.filter(e => !e.is_resolved),
    ppeViolations:  violations.filter(v => !v.acknowledged),
    offlineCameras,
    totalCameras:   cameras.length,
  })

  const levelColor = riskScore.level === 'critical' ? '#ef4444'
    : riskScore.level === 'high'     ? '#f87171'
    : riskScore.level === 'moderate' ? '#fbbf24' : '#34d399'

  return (
    <div className="rounded-2xl p-4" style={{ background: SURFACE.card, border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="text-[9px] font-black uppercase tracking-widest mb-3" style={{ color: TEXT.muted }}>
        Risco Visual
      </div>

      {/* Score ring */}
      <div className="flex items-center gap-4 mb-3">
        <div className="relative w-16 h-16">
          <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
            <circle cx="32" cy="32" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
            <motion.circle cx="32" cy="32" r="24" fill="none"
              stroke={levelColor}
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 24}`}
              animate={{ strokeDashoffset: 2 * Math.PI * 24 * (1 - riskScore.score / 100) }}
              initial={{ strokeDashoffset: 2 * Math.PI * 24 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-black" style={{ color: levelColor }}>{riskScore.score}</span>
          </div>
        </div>

        <div>
          <div className="text-xs font-black capitalize" style={{ color: levelColor }}>
            {riskScore.level === 'low' ? 'OK' : riskScore.level.toUpperCase()}
          </div>
          <div className="text-[9px] mt-1" style={{ color: TEXT.faint }}>Score visual</div>
        </div>
      </div>

      {/* Components */}
      <div className="space-y-1.5">
        {riskScore.components.map(c => (
          <div key={c.label} className="flex items-center gap-2">
            <span className="text-[9px] w-20 shrink-0" style={{ color: TEXT.muted }}>{c.label}</span>
            <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <motion.div className="h-full rounded-full"
                style={{ background: c.score > 50 ? '#f87171' : c.score > 20 ? '#fbbf24' : '#34d399' }}
                animate={{ width: `${c.score}%` }}
                initial={{ width: '0%' }}
                transition={{ duration: 0.8 }}
              />
            </div>
            <span className="text-[9px] w-6 text-right font-black" style={{ color: TEXT.faint }}>{c.score}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────
export default function VisionDashboard() {
  const { company, activeObraId } = useAuth() as any
  const { violations, acknowledgeViolation } = useVisionStore()

  const obraId    = (activeObraId ?? '') as string
  const companyId = (company?.id ?? '') as string
  const stopRef   = useRef<(() => void) | null>(null)

  const [tab, setTab] = useState<'wall' | 'timeline' | 'violations'>('wall')

  useEffect(() => {
    if (!obraId) return
    startVisionEngine({ obraId, companyId }).then(stop => { stopRef.current = stop })
    return () => stopRef.current?.()
  }, [obraId, companyId])

  const pendingViolations = violations.filter(v => !v.acknowledged)

  if (!FF.ENABLE_CAMERA_WALL) {
    return (
      <div className="flex items-center justify-center h-full opacity-40">
        <div className="text-center">
          <Camera size={40} className="mx-auto mb-3" style={{ color: TEXT.muted }} />
          <div className="font-bold text-white">Camera Wall</div>
          <div className="text-sm mt-1" style={{ color: TEXT.muted }}>
            Ative a flag ENABLE_CAMERA_WALL para usar esta funcionalidade
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen" style={{ background: 'hsl(222 25% 5%)', color: 'white' }}>
      {/* Header */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-3"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'rgba(0,0,0,0.3)' }}>
        <Camera size={15} style={{ color: 'hsl(24 95% 55%)' }} />
        <span className="text-sm font-black uppercase tracking-widest">Vision Intelligence</span>
        <span className="text-[9px] px-2 py-0.5 rounded-full"
          style={{ background: 'rgba(96,165,250,0.1)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}>
          COMPUTER VISION
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11px] font-bold"
            style={{ background: 'rgba(249,115,22,0.1)', color: 'hsl(24 95% 55%)', border: '1px solid rgba(249,115,22,0.2)' }}>
            <Plus size={11} /> Adicionar Câmera
          </button>
        </div>
      </div>

      {/* KPI bar */}
      <div className="px-5 py-3 shrink-0">
        <VisionKPIBar />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-5 shrink-0">
        {([
          { key: 'wall',       label: '📷 Camera Wall' },
          { key: 'timeline',   label: '🕐 Timeline Visual' },
          { key: 'violations', label: `🦺 Violações EPI (${pendingViolations.length})` },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="text-[11px] font-bold px-4 py-2 rounded-t-lg transition-all"
            style={{
              background: tab === t.key ? SURFACE.card : 'transparent',
              color:      tab === t.key ? 'white' : TEXT.muted,
              border:     `1px solid ${tab === t.key ? 'rgba(255,255,255,0.08)' : 'transparent'}`,
              borderBottom: tab === t.key ? `1px solid ${SURFACE.card}` : '1px solid transparent',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <AnimatePresence mode="wait">
          {tab === 'wall' && (
            <motion.div key="wall" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex">
              <div className="flex-1 h-full">
                <CameraWall obraId={obraId} />
              </div>
              <div className="w-72 p-4 overflow-y-auto shrink-0"
                style={{ borderLeft: '1px solid rgba(255,255,255,0.06)' }}>
                <VisionRiskPanel />
              </div>
            </motion.div>
          )}

          {tab === 'timeline' && (
            <motion.div key="timeline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full p-4">
              <VisionTimeline obraId={obraId} />
            </motion.div>
          )}

          {tab === 'violations' && (
            <motion.div key="violations" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full p-4 overflow-y-auto">
              {pendingViolations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2 opacity-30">
                  <CheckCircle2 size={32} style={{ color: '#34d399' }} />
                  <span className="text-sm font-bold text-white">Nenhuma violação de EPI pendente</span>
                </div>
              ) : (
                <div className="space-y-2 max-w-xl">
                  {pendingViolations.map(v => (
                    <PPEViolationCard
                      key={v.id}
                      violation={v}
                      onAcknowledge={id => acknowledgeViolation(id)}
                    />
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}
